import React, { useState } from 'react'
import { Star, Quote, ChevronLeft, ChevronRight, Users, Heart } from 'lucide-react'
import Card from '../ui/Card'
import Button from '../ui/Button'

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0)

  const testimonials = [
    {
      id: 1,
      name: "María González",
      role: "Madre de Familia",
      grade: "Estudiante de 3° grado",
      content: "Las Gardenias ha sido una excelente elección para mi hija. Los maestros son muy dedicados y el ambiente educativo es excepcional. He visto un gran progreso en su desarrollo académico y personal.",
      rating: 5,
      image: "👩🏻"
    },
    {
      id: 2,
      name: "Carlos Ramírez",
      role: "Padre de Familia", 
      grade: "Estudiante de 7° grado",
      content: "La formación integral que ofrece la institución es admirable. Mi hijo no solo ha mejorado académicamente, sino que también ha desarrollado valores sólidos y habilidades sociales importantes.",
      rating: 5,
      image: "👨🏻"
    },
    {
      id: 3,
      name: "Ana Sofía Martínez",
      role: "Egresada",
      grade: "Promoción 2023",
      content: "Los años que pasé en Las Gardenias fueron fundamentales para mi formación. Los conocimientos y valores que adquirí me han ayudado mucho en mi siguiente etapa educativa.",
      rating: 5,
      image: "👩🏽"
    },
    {
      id: 4,
      name: "Roberto López",
      role: "Padre de Familia",
      grade: "Estudiante de Transición",
      content: "El nivel de atención personalizada que recibe mi hijo en transición es extraordinario. Las metodologías son apropiadas para su edad y siempre llega feliz a casa.",
      rating: 5,
      image: "👨🏽"
    }
  ]

  const stats = [
    {
      icon: Users,
      number: "95%",
      label: "Satisfacción Familiar",
      description: "De las familias recomiendan nuestra institución"
    },
    {
      icon: Star,
      number: "4.8/5",
      label: "Calificación Promedio",
      description: "En evaluaciones de la comunidad educativa"
    },
    {
      icon: Heart,
      number: "100%",
      label: "Compromiso",
      description: "De nuestro equipo con la excelencia educativa"
    }
  ]

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ))
  }

  return (
    <section className="section-padding bg-gradient-to-b from-white to-gardenia-gold/5">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gardenia-dark mb-6">
            Lo que Dice Nuestra
            <span className="text-gradient block mt-2">Comunidad</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Las opiniones de nuestras familias y estudiantes son nuestro mejor testimonio 
            del compromiso y calidad educativa que ofrecemos día a día.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon
            return (
              <Card key={index} className="text-center" variant="bordered">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green mb-4">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gardenia-red mb-2">
                    {stat.number}
                  </div>
                  <h3 className="text-lg font-semibold text-gardenia-dark mb-2">
                    {stat.label}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {stat.description}
                  </p>
                </div>
              </Card>
            )
          })}
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <Card className="relative overflow-hidden" padding="xl" variant="elevated">
            {/* Quote Icon */}
            <div className="absolute top-6 left-6 opacity-10">
              <Quote className="h-16 w-16 text-gardenia-red" />
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-gardenia-red/10 hover:bg-gardenia-red/20 transition-colors duration-300 z-10"
              aria-label="Testimonio anterior"
            >
              <ChevronLeft className="h-6 w-6 text-gardenia-red" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-gardenia-red/10 hover:bg-gardenia-red/20 transition-colors duration-300 z-10"
              aria-label="Testimonio siguiente"
            >
              <ChevronRight className="h-6 w-6 text-gardenia-red" />
            </button>

            {/* Testimonial Content */}
            <div className="text-center px-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={`transition-all duration-500 ${
                    index === activeTestimonial
                      ? 'opacity-100 transform translate-x-0'
                      : 'opacity-0 transform translate-x-8 absolute inset-0'
                  }`}
                >
                  {/* Avatar */}
                  <div className="mb-6">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
                      {testimonial.image}
                    </div>
                    
                    {/* Stars */}
                    <div className="flex justify-center space-x-1 mb-4">
                      {renderStars(testimonial.rating)}
                    </div>
                  </div>

                  {/* Content */}
                  <blockquote className="text-lg md:text-xl text-gray-700 italic leading-relaxed mb-6 max-w-2xl mx-auto">
                    "{testimonial.content}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gardenia-dark">
                      {testimonial.name}
                    </h4>
                    <p className="text-gardenia-red font-medium">
                      {testimonial.role}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {testimonial.grade}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Indicators */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeTestimonial
                    ? 'bg-gardenia-red scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir al testimonio ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-gardenia-red/5 to-gardenia-green/5 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gardenia-dark mb-4">
              ¿Quieres ser parte de nuestra familia educativa?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Únete a cientos de familias que han confiado en Las Gardenias para 
              la formación integral de sus hijos. ¡Descubre todo lo que podemos ofrecer!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/admisiones" className="btn-primary">
                Conoce Nuestro Proceso
              </Button>
              <Button to="/contacto" className="btn-outline">
                Agenda una Visita
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TestimonialsSection